import React, { useState } from 'react';
import { FormInstance } from 'antd/es/form';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import { AutoCompleteInput } from 'components/base/AutoComplete';
import { moveToFirstError } from 'libs/api/errorHandle';
import { autoCompleteCompanyName, autoCompletePositionName } from 'libs/api/main';
import { CMonthFormat, CUploadFileProps } from 'constants/base.const';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType, EEmploymentType, EWorkCareer } from 'enums/base.enum';
import {
  Button,
  Form,
  Radio,
  Divider,
  Typography,
  Row,
  Col,
  Input,
  Tag,
  DatePicker,
  Tooltip,
  Upload,
  message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { SecondaryColor } from 'GlobalStyles';
import { FormWrapper } from 'GlobalStyles';

const { Text, Title } = Typography;
type TWorkExperienceProps = {
  userType: EUserType;
  form: FormInstance;
  onClickNext?: () => void;
  onFormFinish?: any;
};

const WorkExperience = ({ userType, form, onClickNext, onFormFinish }: TWorkExperienceProps) => {
  const [uploadVisible, setUploadVisible] = useState(true);

  return (
    <FormWrapper style={{ maxWidth: '380px' }}>
      <Tag color={CUserTypeColor[userType]}>{CUserType[userType]}</Tag>
      <Title level={2} style={{ marginBottom: '20px' }}>
        <span style={{ color: `${CUserTypeColor[userType]}` }}>경력사항</span>을
        <br />
        입력해주세요
      </Title>

      <Form
        name='mentor_type'
        form={form}
        layout='vertical'
        initialValues={{ mentor_type: EWorkCareer.Junior }}
        className='form-inline'
      >
        {/* 경력개요 */}
        {CUserType[userType] === '멘토' && (
          <div>
            <Text style={{ display: 'block', marginBottom: '10px' }} strong>
              경력개요
              <Tooltip
                placement='top'
                title={
                  <div style={{ fontSize: '12px', lineHeight: 0.7 }}>
                    <p style={{ marginTop: '1em' }}>주니어 - 컨설팅펌 채용 면접관 경험 미보유 시</p>
                    <p>시니어 - 컨설팅펌 채용 면접관 경험 보유 시</p>
                  </div>
                }
                color={`${SecondaryColor}` as 'green'}
              >
                <QuestionCircleOutlined style={{ marginLeft: '10px' }} />
              </Tooltip>
            </Text>
            <Form.Item
              name='mentor_type'
              // name={[field.name, 'career_type']}
              className='form-item form-item-no-label'
              rules={[{ required: true }]}
            >
              <Radio.Group className='col-12'>
                <Radio.Button value={EWorkCareer.Junior}>주니어</Radio.Button>
                <Radio.Button value={EWorkCareer.Senior}>시니어</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>
        )}
      </Form>

      <Form
        name='work_experience'
        form={form}
        layout='vertical'
        initialValues={{
          work_experiences: [{ employment_type: EEmploymentType.PartTime }],
        }}
        className='form-inline'
      >
        {CUserType[userType] === '멘티' && (
          <div>
            <Text style={{ display: 'block', marginBottom: '10px' }} strong>
              이력서
            </Text>
            <Form.Item
              name={'resume_files'}
              className='form-item'
              rules={[
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (getFieldValue('activate_intern') === true && (!value || value?.length == 0)) {
                      return Promise.reject('이력서를 업로드해주세요');
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.resume_files !== currentValues.resume_files
              }
              valuePropName='fileList'
              label='이력서 업로드'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/resume/file`}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;

                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                    setUploadVisible(false);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  } else if (status === 'removed') {
                    if (info.fileList.length === 0) {
                      setUploadVisible(true);
                    }
                  }
                }}
              >
                {uploadVisible === true && (
                  <div style={{ float: 'right' }} className='input-suffix'>
                    업로드
                  </div>
                )}
              </Upload>
            </Form.Item>
            <Divider />
          </div>
        )}

        {/* 경력 사항 */}
        <Text style={{ display: 'block', marginBottom: '10px' }} strong>
          경력사항
        </Text>
        <Form.List name='work_experiences'>
          {(fields, { add, remove }) => (
            <div>
              {fields.map((field, index) => (
                <Form.Item key={field.key} noStyle={true}>
                  <Form.Item
                    name={[field.name, 'employment_type']}
                    className='form-item form-item-no-label'
                    rules={[{ required: true }]}
                  >
                    <Radio.Group className='col-12'>
                      <Radio.Button value={EEmploymentType.PartTime}>인턴</Radio.Button>
                      <Radio.Button value={EEmploymentType.FullTime}>정규직</Radio.Button>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item noStyle>
                    <Row>
                      <Col flex='auto'>
                        <Form.Item
                          label='회사명'
                          name={[field.name, 'company_name']}
                          rules={[{ required: true }]}
                          className='form-item'
                        >
                          <AutoCompleteInput
                            promise={autoCompleteCompanyName}
                            form={form}
                            name={[field.name, 'company_name']}
                          >
                            <Input autoComplete='off' size='large' placeholder='회사명을 입력해주세요' />
                          </AutoCompleteInput>
                        </Form.Item>
                      </Col>
                      {fields.length > 1 && (
                        <Col flex='50px'>
                          <div
                            className='list-delete-button'
                            onClick={() => {
                              remove(field.name);
                            }}
                          >
                            <DeleteOutlined />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'position']}
                    className='form-item'
                    label='직급'
                    rules={[{ required: true }]}
                  >
                    <AutoCompleteInput
                      promise={autoCompletePositionName}
                      form={form}
                      name={[field.name, 'position']}
                    >
                      <Input autoComplete='off' size='large' placeholder='직급을 입력해주세요' />
                    </AutoCompleteInput>
                  </Form.Item>
                  <Form.Item noStyle>
                    <Input.Group>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Form.Item
                            name={[field.name, 'date_from']}
                            className='form-item'
                            label='시작일'
                            rules={[{ required: true }]}
                          >
                            <DatePicker picker='month' format={CMonthFormat} placeholder='시작일 선택' />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name={[field.name, 'date_to']} className='form-item' label='종료일'>
                            <DatePicker picker='month' format={CMonthFormat} placeholder='종료일 선택' />
                          </Form.Item>
                        </Col>
                      </Row>
                      <span
                        style={{
                          marginTop: '-10px',
                          float: 'right',
                          fontSize: '12px',
                          color: `${SecondaryColor}`,
                        }}
                      >
                        현직이면 비워주세요
                      </span>
                    </Input.Group>
                  </Form.Item>
                </Form.Item>
              ))}
              <Form.Item noStyle={true}>
                <Divider />
                <div className='list-plus-button'>
                  <Button
                    onClick={() => {
                      add({ employment_type: EEmploymentType.PartTime });
                    }}
                  >
                    <PlusOutlined />
                  </Button>
                </div>
              </Form.Item>
            </div>
          )}
        </Form.List>
        <Form.Item shouldUpdate={true} noStyle={true}>
          {() => (
            <div className='steps-action'>
              {userType === 'mentee' ? (
                <Row gutter={[4, 0]}>
                  <Col span={6}>
                    <Button
                      htmlType='submit'
                      className='btn-center'
                      //나중에 하기 눌러서 그냥 이 파트 넘어갈 때는 임의로
                      onClick={() => {
                        form.setFields([{ name: 'work_experiences', value: [] }]);
                      }}
                    >
                      경력 없음
                    </Button>
                  </Col>
                  <Col span={18}>
                    <Button
                      type='primary'
                      htmlType='submit'
                      size='large'
                      //유효성검사까지 하고 submit
                      onClick={() => {
                        form.validateFields().catch((errorInfo) => {
                          moveToFirstError(form, errorInfo.errorFields);
                        });
                      }}
                    >
                      완료
                    </Button>
                  </Col>
                </Row>
              ) : (
                //멘토 가입일 때
                <Row gutter={[4, 0]}>
                  <Col span={6}>
                    <Button
                      className='btn-center'
                      onClick={() => {
                        onClickNext && onClickNext();
                      }}
                    >
                      경력 없음
                    </Button>
                  </Col>
                  <Col span={18}>
                    <Button
                      type='primary'
                      htmlType='submit'
                      size='large'
                      onClick={() => {
                        form.validateFields().catch((errorInfo) => {
                          moveToFirstError(form, errorInfo.errorFields);
                        });
                      }}
                    >
                      다음
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Form.Item>
      </Form>
    </FormWrapper>
  );
};

export default WorkExperience;
